import classNames from 'classnames';
import React, { useState } from 'react';
import EcoTitle, { EcoTitleProps } from 'eco-design/base/eco-title';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
// import 'swiper/css/pagination';
import './index.scss';
import EcoImgVideo from 'eco-design/base/eco-img-video';
import EcoTab from 'eco-design/elements/eco-tab';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import { Navigation, Pagination } from 'swiper';
import gtm from 'eco-design-reliance/gtm';

export interface EcoTabsSwiperItem {
    tabLabel: string;
    tabValue: string;
    description?: React.ReactNode;
    isVideo?: boolean;

    img: {
        pc?: string;
        pad?: string;
        h5?: string;
        isVideo?: boolean;
        poster?: string;
        posterH5?: string;
        isOpenLazy?: boolean;
    };
}

export interface EcoTabsSwiperProps {
    className?: string;
    theme?: 'white' | 'black';
    title?: EcoTitleProps['title'];
    items?: EcoTabsSwiperItem[];
}

/**
 * 维护: 杨柏迪
 * 描述: tab-swiper 切换
 */
const DEFAULT_ITEMS: EcoTabsSwiperProps['items'] = [];
const EcoTabsSwiper: React.FC<EcoTabsSwiperProps> = ({ className, title, theme = 'white', items = DEFAULT_ITEMS }) => {
    const prefixCls = `eco-tabs-swiper`;
    const classes = classNames(prefixCls, className);
    const [swiper, setSwiper] = useState<any>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    // 初始化 Swiper 时设置索引
    const handleSwiper = (swiperInstance: any) => {
        setSwiper(swiperInstance);
        setCurrentIndex(swiperInstance.realIndex);
    };

    // Tab 切换时同步 Swiper
    const handleTabChange = (_: unknown, index: number) => {
        if (swiper) {
            // 在 loop 模式下使用 slideToLoop，确保索引正确对应
            if (swiper.params.loop) {
                swiper.slideToLoop(index);
            } else {
                swiper.slideTo(index);
            }
        }
    };

    // Swiper 切换时同步 Tab
    const handleSlideChange = (swiper: any) => {
        // 使用 realIndex 而不是 activeIndex，因为 loop 模式会创建克隆的 slides
        setCurrentIndex(swiper.realIndex);
    };

    const handleGtm = (type: 'left' | 'right') => {
        const btnId1 = type === 'left' ? `${prefixCls} .${prefixCls}-swiper-prev` : `${prefixCls} .${prefixCls}-swiper-next`;
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: title || 'none',
            button_name: type === 'left' ? 'Left switch' : 'Right switch',
            __btn_id1: btnId1,
            __btn_id2: `arrow-${type}`,
            __btn_type: 'common',
        });
    };

    return (
        <div className={classes}>
            {title && <EcoTitle className={`${prefixCls}-title`} title={title} />}
            {items.length > 0 && (
                <div className={`${prefixCls}-swiper-container`}>
                    <div className={`${prefixCls}-swiper-wrapper`}>
                        <Swiper
                            className={`${prefixCls}-swiper`}
                            loop={items.length > 1}
                            navigation={{
                                nextEl: `.${prefixCls}-swiper-next`,
                                prevEl: `.${prefixCls}-swiper-prev`,
                            }}
                            pagination={{
                                el: `.${prefixCls}-swiper-pagination`,
                                clickable: true,
                            }}
                            modules={[Navigation, Pagination]}
                            onSwiper={handleSwiper}
                            onSlideChange={handleSlideChange}
                            initialSlide={0}
                        >
                            {items.map((item, idx) => (
                                <SwiperSlide key={idx}>
                                    <div className={`${prefixCls}-swiper-slide-content`}>
                                        <div className={`${prefixCls}-swiper-slide-img-box`}>
                                            <EcoImgVideo {...item.img} isVideo={item?.isVideo || false} />
                                        </div>
                                        {items.length > 0 && items[currentIndex]?.description && (
                                            <div className={`${prefixCls}-description-m`}>{items[currentIndex].description}</div>
                                        )}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {items.length > 1 && (
                            <>
                                <div className={`${prefixCls}-swiper-prev GtmModuleData-btn-common`} data-btn-id="arrow-left" onClick={() => handleGtm('left')}>
                                    <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                </div>
                                <div
                                    className={`${prefixCls}-swiper-next GtmModuleData-btn-common`}
                                    data-btn-id="arrow-right"
                                    onClick={() => handleGtm('right')}
                                >
                                    <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                </div>
                            </>
                        )}
                    </div>
                    {items.length > 1 && <div className={`${prefixCls}-swiper-pagination`}></div>}
                </div>
            )}
            {items.length > 0 && (
                <div className={`${prefixCls}-tab`}>
                    <EcoTab onChange={handleTabChange} activeIndex={currentIndex} gtmTitle={title as string} items={items} theme={theme} />
                </div>
            )}
            {items.length > 0 && items[currentIndex]?.description && <div className={`${prefixCls}-description`}>{items[currentIndex].description}</div>}
        </div>
    );
};

export default EcoTabsSwiper;
